import { createApi } from '@reduxjs/toolkit/query/react';
import deserialize from 'helpers/getroPropertyMapper';

import { networkBaseQueryV2 } from './queries';
import { updateAllContactsCache } from './contactsV2';
import { contactsApi } from './contacts';
import { sharedListViewsV2Api } from './sharedListViewsV2';

/**
 * Builds cache keys for custom fields queries based on the mutation argument.
 * For list-specific fields, only returns the specific list's cache.
 * For global fields, returns all active query caches.
 */
const buildCustomFieldsCacheKeys = (state, arg) => {
  const customFieldsApiState = state.customFieldsV2Api;
  const cacheKeys = [];

  Object.keys(customFieldsApiState?.queries || {}).forEach((queryCacheKey) => {
    const query = customFieldsApiState.queries[queryCacheKey];
    if (query?.endpointName === 'getCustomFields') {
      // For list-specific fields, only update the specific list's cache
      if (arg.sharedListId) {
        // Normalize both values to strings for comparison (listId can be string or number)
        const argListId = String(arg.sharedListId);
        const queryListId = String(query.originalArgs?.sharedListId);
        if (queryListId === argListId) {
          cacheKeys.push(query.originalArgs);
        }
      } else {
        // For global fields, update all caches
        cacheKeys.push(query.originalArgs);
      }
    }
  });

  return cacheKeys;
};

export const customFieldsV2Api = createApi({
  reducerPath: 'customFieldsV2Api',
  baseQuery: networkBaseQueryV2,
  tagTypes: ['custom-fields'],
  endpoints: (builder) => ({
    getCustomFields: builder.query({
      query: (params) => ({
        url: '/custom_fields',
        params,
      }),
      transformResponse: (response) => deserialize(response),
      providesTags: ['custom-fields'],
    }),
    createCustomField: builder.mutation({
      query: (data) => ({
        url: '/custom_fields',
        method: 'POST',
        data,
      }),
      transformResponse: (response) => deserialize(response),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        let patchResults = [];
        let contactsCacheUpdates;
        const listDetailPatchResults = [];

        try {
          const result = await queryFulfilled;
          const state = getState();
          const cacheKeys = buildCustomFieldsCacheKeys(state, arg);

          patchResults = cacheKeys.map((cacheKey) =>
            dispatch(
              customFieldsV2Api.util.updateQueryData('getCustomFields', cacheKey, (draft) => {
                if (draft && Array.isArray(draft)) {
                  draft.push(result.data);
                }
              }),
            ),
          ); // eslint-disable-line function-paren-newline

          contactsCacheUpdates = updateAllContactsCache({
            state: getState(),
            dispatch,
            getChanges: (contact) => ({
              customFields: [
                ...(contact.customFields || []),
                {
                  id: result.data.id,
                  name: result.data.name,
                  ...(arg.sharedListId
                    ? {
                        sharedList: {
                          id: arg.sharedListId.toString(),
                        },
                      }
                    : {}),
                  value: null,
                },
              ],
            }),
          });

          // Update fieldSettings in the shareable view of list detail caches
          const contactsApiState = getState().contactsApi;
          const updateSharedListViewPromises = [];

          Object.keys(contactsApiState?.queries || {}).forEach((queryCacheKey) => {
            const query = contactsApiState.queries[queryCacheKey];
            if (query?.endpointName === 'getSharedList' && query?.data?.sharedList?.views) {
              const listId = query.originalArgs?.listId;

              // For list-specific fields, only update the specific list's cache
              // For global fields, update all list caches
              if (arg.sharedListId && String(arg.sharedListId) !== String(listId)) {
                return;
              }

              const shareableView = query.data.sharedList.views.find((view) => view.shareable === true);
              if (shareableView?.fieldSettings) {
                const patchResult = dispatch(
                  contactsApi.util.updateQueryData('getSharedList', query.originalArgs, (draft) => {
                    if (!draft.sharedList?.views) return;
                    const viewIndex = draft.sharedList.views.findIndex((v) => v.shareable === true);
                    if (viewIndex !== -1 && draft.sharedList.views[viewIndex].fieldSettings) {
                      draft.sharedList.views[viewIndex].fieldSettings.push({
                        fieldId: result.data.id,
                        visible: false,
                      });
                    }
                  }),
                );
                listDetailPatchResults.push(patchResult);

                // Make API call to persist the updated fieldSettings
                const updatedFieldSettings = [
                  ...shareableView.fieldSettings.map((setting) => ({
                    fieldId: setting.fieldId,
                    visible: setting.visible,
                  })),
                  { fieldId: result.data.id, visible: false },
                ];
                const updatePromise = dispatch(
                  sharedListViewsV2Api.endpoints.updateSharedListView.initiate({
                    sharedListId: listId,
                    id: shareableView.id,
                    data: { fieldSettings: updatedFieldSettings },
                  }),
                ).unwrap();
                updateSharedListViewPromises.push(updatePromise);
              }
            }
          });

          // Await all updateSharedListView calls to ensure failures trigger rollback
          await Promise.all(updateSharedListViewPromises);
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
          contactsCacheUpdates?.forEach((update) => update?.undo());
          listDetailPatchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),
    updateCustomField: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/custom_fields/${id}`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response) => deserialize(response),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const state = getState();
        const cacheKeys = buildCustomFieldsCacheKeys(state, arg);

        const patchResults = cacheKeys.map((cacheKey) =>
          dispatch(
            customFieldsV2Api.util.updateQueryData('getCustomFields', cacheKey, (draft) => {
              if (draft && Array.isArray(draft)) {
                const fieldIndex = draft.findIndex((field) => field.id === arg.id);
                if (fieldIndex !== -1) {
                  Object.assign(draft[fieldIndex], {
                    ...draft[fieldIndex],
                    ...arg,
                  });
                }
              }
            }),
          ),
        ); // eslint-disable-line function-paren-newline

        updateAllContactsCache({
          state: getState(),
          dispatch,
          getChanges: (contact) => {
            if (!contact.customFields) return {};

            const hasChanges = contact.customFields.some(
              (field) => field.id === arg.id && Object.keys(arg).some((key) => field[key] !== arg[key]),
            );

            if (!hasChanges) return {};

            const customFields = contact.customFields.map((field) => {
              if (field.id === arg.id) {
                const updatedField = { ...field, name: arg.name };

                if (arg.type === 'single_select') {
                  const opt = arg.params.options.find((option) => option.id === updatedField.value);

                  return opt ? { ...updatedField, value: opt.id } : { ...updatedField, value: null };
                }

                return updatedField;
              }

              return field;
            });

            return {
              customFields,
            };
          },
        });

        try {
          const result = await queryFulfilled;

          cacheKeys.forEach((cacheKey) => {
            dispatch(
              customFieldsV2Api.util.updateQueryData('getCustomFields', cacheKey, (draft) => {
                if (draft && Array.isArray(draft)) {
                  const fieldIndex = draft.findIndex((field) => field.id === arg.id);
                  if (fieldIndex !== -1) {
                    Object.assign(draft[fieldIndex], {
                      ...draft[fieldIndex],
                      ...result.data,
                    });
                  }
                }
              }),
            );
          });
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),
    deleteCustomField: builder.mutation({
      query: ({ id }) => ({
        url: `/custom_fields/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const state = getState();
        const cacheKeys = buildCustomFieldsCacheKeys(state, arg);

        const patchResults = cacheKeys.map((cacheKey) =>
          dispatch(
            customFieldsV2Api.util.updateQueryData('getCustomFields', cacheKey, (draft) => {
              if (draft && Array.isArray(draft)) {
                const fieldIndex = draft.findIndex((field) => field.id === arg.id);
                if (fieldIndex !== -1) {
                  draft.splice(fieldIndex, 1);
                }
              }
            }),
          ),
        ); // eslint-disable-line function-paren-newline

        const contactsCacheUpdates = updateAllContactsCache({
          state: getState(),
          dispatch,
          getChanges: (contact) => {
            if (!contact.customFields) return {};

            return {
              customFields: contact.customFields.filter((field) => field.id !== arg.id),
            };
          },
        });

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
          contactsCacheUpdates?.forEach((update) => update?.undo());
        }
      },
    }),
  }),
});

export const {
  useGetCustomFieldsQuery,
  useCreateCustomFieldMutation,
  useUpdateCustomFieldMutation,
  useDeleteCustomFieldMutation,
} = customFieldsV2Api;

const getCustomFieldsDefaultValues = ({ type, id, params }) => {
  if (type === 'single_select') {
    return {
      editable: true,
      cellEditor: 'agRichSelectCellEditor',
      cellRenderer: 'customFieldSingleSelectRenderer',
      singleClickEdit: true,
      cellEditorParams: {
        values: params?.options || [],
        cellRenderer: 'customFieldSingleSelectRowRenderer',
        suppressKeyboardEvent: (eventParams) => eventParams.event.key === 'Escape' || eventParams.event.key === 'Enter',
        stopEditingOnEnter: true,
        allowTyping: false,
        popupParent: document.body,
      },
      filterList: true,
      highlightMatch: true,
      valueGetter: ({ data }) => {
        const value = data?.customFields?.find((field) => field.id === id)?.value;
        const opt = params?.options?.find((option) => option.id === value);

        return opt;
      },
      valueFormatter: ({ value }) => value?.label || value || '',
    };
  }
  return {
    editable: true,
    cellEditor: 'agTextCellEditor',
    cellRenderer: 'customFieldTextRenderer',
    singleClickEdit: true,
    useFormatter: true,
    valueGetter: ({ data }) => data?.customFields?.find((field) => field.id === id)?.value || '',
  };
};

export const createTableColumn = (field) => ({
  Header: field.name,
  accessor: `custom-field-${field.id}`,
  width: 240,
  disableSortBy: true,
  disablePadding: true,
  show: true,
  customField: field,
  pinned: false,
  ...getCustomFieldsDefaultValues(field),
});
